import React, { useEffect, useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Pulse, ArrowsClockwise, X, PencilSimple, Trash, Stack } from '@phosphor-icons/react';
import api from '../lib/api';

const ACTION_LABELS = {
  'forecast_override.upsert': { label: 'Override saved', Icon: PencilSimple, tone: 'text-brand' },
  'forecast_override.delete': { label: 'Override cleared', Icon: Trash, tone: 'text-rose-600' },
  'forecast_override.bulk_upsert': { label: 'Bulk override', Icon: Stack, tone: 'text-emerald-600' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function parseResourceId(rid = '') {
  // Format: "<sku_id>:w<n>:<field>" — we only show the trailing parts to
  // keep the row tidy, since sku_id is an opaque ObjectId.
  const m = String(rid).match(/^([^:]+):w(\d+):(\w+)$/);
  if (!m) return { sku: rid, week: '', field: '' };
  return { sku: m[1], week: `W${parseInt(m[2], 10) + 1}`, field: m[3] };
}

export default function OverrideAuditFeed({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pull last 50 cell-override actions across the three event types. We
      // request the union via a single call with a wildcard-ish filter — the
      // /api/audit-logs endpoint only supports an exact `action` match, so
      // we issue 3 parallel calls and merge.
      const [up, del, bulk] = await Promise.all([
        api.get('/audit-logs', { params: { action: 'forecast_override.upsert', limit: 30 } }),
        api.get('/audit-logs', { params: { action: 'forecast_override.delete', limit: 30 } }),
        api.get('/audit-logs', { params: { action: 'forecast_override.bulk_upsert', limit: 30 } }),
      ]);
      const merged = [
        ...(up.data?.items || []),
        ...(del.data?.items || []),
        ...(bulk.data?.items || []),
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 50);
      setRows(merged);
    } catch (e) {
      // 401 is fine — admin role required; fall back to empty list silently
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="override-audit-feed"
      >
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Pulse size={18} weight="bold" className="text-brand" />
              Override Activity
            </SheetTitle>
            <button
              onClick={load}
              disabled={loading}
              className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-accent"
              data-testid="override-audit-refresh"
              title="Refresh"
            >
              <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Recent forecast-override edits across your team. Admin-only — drawn from the audit log.
          </p>
        </SheetHeader>

        <div className="mt-4 space-y-2">
          {loading && rows.length === 0 ? (
            <div className="text-sm text-muted-foreground italic text-center py-12">Loading…</div>
          ) : rows.length === 0 ? (
            <div data-testid="override-audit-empty" className="text-sm text-muted-foreground italic text-center py-12">
              No override activity yet. Click any matrix cell to make an edit — it'll show up here.
            </div>
          ) : rows.map((r, i) => {
            const meta = ACTION_LABELS[r.action] || { label: r.action, Icon: PencilSimple, tone: 'text-muted-foreground' };
            const Icon = meta.Icon;
            const parsed = parseResourceId(r.resource_id);
            const before = r.details?.before;
            const after = r.details?.after;
            return (
              <div
                key={`${r.timestamp}-${i}`}
                data-testid={`override-audit-row-${i}`}
                className="bg-card border border-border rounded-md p-3"
              >
                <div className="flex items-start gap-2">
                  <div className={`mt-0.5 ${meta.tone} shrink-0`}>
                    <Icon size={14} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] font-semibold text-foreground">{meta.label}</span>
                      {parsed.week && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {parsed.week} · {parsed.field}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-auto">{timeAgo(r.timestamp)}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      {r.user_email || 'system'}
                    </div>
                    {/* before → after diff for single-cell events */}
                    {(before || after) && r.action !== 'forecast_override.bulk_upsert' && (
                      <div className="text-[11px] font-mono text-foreground mt-1 flex items-center gap-1.5">
                        {before?.value !== undefined && (
                          <span className="line-through text-muted-foreground">{before.value}</span>
                        )}
                        {before?.value !== undefined && after?.value !== undefined && <span className="text-muted-foreground">→</span>}
                        {after?.value !== undefined && (
                          <span className="text-brand font-semibold">{after.value}</span>
                        )}
                      </div>
                    )}
                    {/* Bulk summary */}
                    {r.action === 'forecast_override.bulk_upsert' && after && (
                      <div className="text-[11px] font-mono text-foreground mt-1">
                        {after.written} cells · <span className="text-muted-foreground">{after.mode}</span>
                        {after.mode === 'percent' ? `: +${after.value}%` : ` = ${after.value}`}
                      </div>
                    )}
                    {r.details?.note && (
                      <div className="text-[11px] italic text-muted-foreground mt-1 truncate">"{r.details.note}"</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {rows.length >= 50 && (
          <div className="text-[10px] text-muted-foreground italic text-center mt-4">
            Showing the most recent 50 entries — older audit rows live in /audit-log.
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function OverrideAuditTrigger({ onClick }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onClick}
      data-testid="override-audit-trigger"
      className="gap-1.5"
    >
      <Pulse size={14} weight="bold" />
      Activity
    </Button>
  );
}
